import React from "react";
import { v4 as uuid } from "uuid";

import { usePlayContext } from "../../pages/Play/play-context";
import styles from "./action-lines.module.scss";
import { useActionPath } from "./hooks";
import { ArrowMarker } from "./markers";
import { ActionLineProps } from "./types";

const SKATE_BACKWARDS_SIZE = 10;

function subdividePath(path: any, stepSize = 16, startOffset = 0) {
  const p = document.createElementNS("http://www.w3.org/2000/svg", "path");
  p.setAttribute("d", path);

  const numOfPoints = Math.floor(p.getTotalLength() / stepSize + 1);
  const indexToStart = Math.floor(startOffset * numOfPoints);
  const points = Array.from(Array(numOfPoints), (_, i) => i)
    .slice(indexToStart)
    .map((i) => p.getPointAtLength(i * stepSize));
  const initialOffset = p.getPointAtLength(0);
  return `M ${initialOffset.x} ${initialOffset.y} ${points.map(({ x, y }) => `L ${x} ${y}`).join(" ")}`;
}

export interface SkateBackwardsProps extends ActionLineProps {
  size?: number;
}

export const SkateBackwards = ({
  action,
  controlPoints,
  strokeWidth = 0.3,
  size = 1,
  animateable = false,
}: SkateBackwardsProps) => {
  const { color = "#000" } = action;
  const markerEndId = uuid();

  const { straightPathData, pathsWithScales, endTime } = useActionPath({ action, controlPoints });

  const { animationTimerApi } = usePlayContext();
  const { effectiveTime: time } = animationTimerApi;

  return (
    <>
      <defs>
        <marker
          id={`skate-backwards-marker-${markerEndId}`}
          viewBox={`-1 0 ${SKATE_BACKWARDS_SIZE * 2} ${SKATE_BACKWARDS_SIZE * 2}`}
          refX={SKATE_BACKWARDS_SIZE}
          refY={SKATE_BACKWARDS_SIZE}
          markerUnits="userSpaceOnUse"
          markerWidth={12 * size}
          markerHeight={12 * size}
          orient="auto"
          fill="none"
          stroke={color}
          strokeWidth={0.25 * (1 - size) + 3}
        >
          <path
            d={`M 0 ${SKATE_BACKWARDS_SIZE} C 0 0, ${SKATE_BACKWARDS_SIZE} 0, ${SKATE_BACKWARDS_SIZE} ${SKATE_BACKWARDS_SIZE}`}
          />
          <path
            d={`M ${SKATE_BACKWARDS_SIZE / 2} ${SKATE_BACKWARDS_SIZE} C ${SKATE_BACKWARDS_SIZE / 2} ${
              SKATE_BACKWARDS_SIZE * 2
            }, ${1.5 * SKATE_BACKWARDS_SIZE} ${SKATE_BACKWARDS_SIZE * 2}, ${
              1.5 * SKATE_BACKWARDS_SIZE
            } ${SKATE_BACKWARDS_SIZE}`}
          />
        </marker>
      </defs>
      <ArrowMarker id={markerEndId} color={color} />
      <path
        d={straightPathData}
        markerEnd={`url(#arrow-${markerEndId})`}
        stroke="none"
        fill="none"
        strokeWidth={strokeWidth}
        opacity={animateable && endTime && time >= endTime ? 0.2 : 1}
      />
      {pathsWithScales.map((path, i) => (
        <g key={i}>
          <path
            d={subdividePath(path.pathString, 12 * size, animateable ? path.pathPercentageScale(time) : 0)}
            className={styles.actionLine}
            markerMid={`url(#skate-backwards-marker-${markerEndId})`}
            stroke="transparent"
            strokeWidth={strokeWidth}
          />
          <path
            d={subdividePath(path.pathString, 12 * size)}
            className={styles.actionLine}
            markerMid={`url(#skate-backwards-marker-${markerEndId})`}
            stroke="transparent"
            strokeWidth={strokeWidth}
            opacity={0.2}
          />
        </g>
      ))}
    </>
  );
};
